import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, ErrorState,
    CopperButton, SlateButton, Field, TextInput, NumberInput, Select, Pill, Modal,
} from "@/components/common/UI";
import FormulaInfo from "@/components/common/FormulaInfo";
import { VarianceBadge } from "@/components/zbc/VarianceBadge";
import { useAuth, hasRole } from "@/lib/auth";
import { toast } from "sonner";
import { fmtINR, fmtIN, fmtPct, fmtDate } from "@/lib/format";
import {
    computeZbcLive, computeVariance, computeOpCost,
    findCuttingRate, findWeldingRate,
} from "@/lib/zbcCalc";
import {
    deriveDensity, deriveRmCategoryFromGrade, deriveScrapCodeFromGrade,
    sheetInputWeightWithOffal, roundBarInputWeightDetail, squareTubeInputWeight,
    deriveFinishWeight, extractScrapAllowances, deriveCutPerimeter,
    deriveCuttingProcess, deriveFilletSize, deriveMachineFromOpName,
    buildAutoCalcConfig, DEFAULT_AUTOCALC_CFG, isMachinedPart,
} from "@/lib/zbcAutoCalc";
import AutoTip from "@/components/common/AutoTip";
import {
    User, FileText, Hammer, Cog, Scissors, Flame, Target,
    Check, ArrowRight, ArrowLeft, Plus, X, AlertTriangle, Sparkles, Info, Search, Copy,
} from "lucide-react";

const RM_OPTIONS = [
    { value: "SHEET_STEEL", label: "Sheet Steel", desc: "Flat plate/sheet cut from a standard sheet" },
    { value: "SQUARE_TUBE", label: "Square Tube", desc: "Cut from square or rectangular tube" },
    { value: "ROUND_BAR", label: "Round Bar", desc: "Cut from round bar: pins, bosses, machined parts" },
    { value: "FILL_MATERIAL", label: "Fill Material", desc: "Counterweight fill (cement/hematite), no cutting" },
    { value: "BOP_PURCHASED", label: "BOP Purchased", desc: "Fully bought-in: supplier price only" },
];
const UOM_LABEL = {
    PER_HOUR: "Time (hours)", PER_KG: "Weight (kg)", PER_BOX: "Parts per Box",
    PER_PIECE: "Quantity", LUMP: "—", PER_STROKE: "Number of Strokes",
};
const SECTIONS = [
    { key: "identity", label: "Part Identity", icon: User },
    { key: "material", label: "Material Cost", icon: Hammer },
    { key: "conversion", label: "Conversion Cost", icon: Cog },
    { key: "cutting", label: "Cutting Cost", icon: Scissors },
    { key: "welding", label: "Welding Cost", icon: Flame },
    { key: "result", label: "Should-Cost & Variance", icon: Target },
];

const emptyForm = {
    part_number: "", navision_code: "", part_name: "", drawing_number: "",
    customer_id: "", plant_id: "", qty_per_bom: 1, rm_category: "SHEET_STEEL",
    part_use: "", notes: "",
    steel_grade_id: "", scrap_rate_id: "",
    rm_rate_override: "", scrap_rate_override: "",
    blank_width_mm: "", blank_length_mm: "", thickness_mm: "",
    bar_od_mm: "", bar_length_mm: "", part_od_mm: "", part_id_mm: "", part_length_mm: "",
    tube_size: "", tube_length_mm: "",
    input_weight_kg: "", finish_weight_kg: "",
    fill_materials: [], bop_price: "",
    // --- Section 2 lump-sum entry (Volvo SUMPRODUCT scenarios) ---
    rm_entry_mode: "CALCULATE",  // CALCULATE | LUMP
    rm_lump_cost: "",
    rm_lump_note: "",
    // --- Section 2B filling material (counterweight shells) ---
    filling_material_enabled: false,
    shell_volume_cum: "",
    pouring_density_kg_cum: "",
    filling_components: [],
    operations: [],
    cutting_process: "LASER_CUTTING", cut_perimeter_mtr: "",
    has_welding: false, weld_joints: [],
    // P0.2 · Tooling / CAPEX (per-piece amortisation)
    tooling_cost_rs: "", annual_volume: "", amortization_years: 3,
    // P0.3 · Child ZBCs (sub-assembly BOM)
    child_zbc_refs: [],
    supplier_id: "", supplier_name: "", supplier_quote: "", quote_date: "",
    status: "DRAFT",
};

export default function ZBCForm() {
    const { user } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    // Separation of duties: only Corporate Admin can save-and-verify in one
    // step. Finance/Pricing must save as Draft, then have a different
    // reviewer verify it from the ZBC detail page; the backend enforces
    // this too (create_zbc/update_zbc reject status=VERIFIED otherwise).
    const canSelfVerify = hasRole(user, "CORPORATE_ADMIN");
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEdit);

    // Masters
    const [customers, setCustomers] = useState([]);
    const [plants, setPlants] = useState([]);
    const [grades, setGrades] = useState([]);
    const [scraps, setScraps] = useState([]);
    const [processRates, setProcessRates] = useState([]);
    const [machines, setMachines] = useState([]);
    const [approvedRates, setApprovedRates] = useState([]);
    const [conversionOps, setConversionOps] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/customers"),
            api.get("/plants"),
            api.get("/steel-grades"),
            api.get("/scrap-rates"),
            api.get("/process-rates"),
            api.get("/machines"),
            api.get("/machine-rates?status=APPROVED"),
            api.get("/conversion-operations"),
        ]).then(([c, p, g, s, pr, m, mr, co]) => {
            setCustomers(c.data);
            setPlants(p.data);
            setGrades(g.data);
            setScraps(s.data);
            setProcessRates(pr.data);
            setMachines(m.data);
            setApprovedRates(mr.data);
            setConversionOps(co.data);
        });
        if (isEdit) {
            Promise.all([
                api.get(`/zbcs/${id}`),
                api.get(`/zbcs/${id}/edit-context`).catch(() => ({ data: { has_ai_source: false } })),
            ]).then(([zbcRes, ctxRes]) => {
                const merged = { ...emptyForm, ...zbcRes.data };
                const ctx = ctxRes.data || {};
                // If the ZBC was born from the AI wizard, prefill dimension
                // fields from the AI-extracted geometry / blank when the ZBC
                // itself doesn't already have them set.  Enables edit-form to
                // show the drawing dimensions users originally uploaded.
                if (ctx.has_ai_source) {
                    const g = ctx.geometry || {};
                    const b = ctx.blank || {};
                    const fv = (x) => (x && typeof x === "object" ? x.value : x);
                    const pickNum = (a, b_, c) => {
                        for (const v of [a, b_, c]) {
                            const n = parseFloat(v);
                            if (!isNaN(n) && n > 0) return v;
                        }
                        return "";
                    };
                    merged.blank_length_mm = merged.blank_length_mm ||
                        pickNum(b.blank_length_mm, fv(g.length_mm), fv(g.length));
                    merged.blank_width_mm = merged.blank_width_mm ||
                        pickNum(b.blank_width_mm, fv(g.width_mm), fv(g.width));
                    merged.thickness_mm = merged.thickness_mm ||
                        pickNum(b.blank_thickness_mm, fv(g.thickness_mm), fv(g.thickness));
                    merged.bar_od_mm = merged.bar_od_mm ||
                        pickNum(b.blank_diameter_mm, fv(g.diameter_mm));
                    merged.bar_length_mm = merged.bar_length_mm ||
                        pickNum(b.blank_length_mm, fv(g.length_mm));
                    merged.part_length_mm = merged.part_length_mm ||
                        pickNum(fv(g.length_mm), fv(g.height_mm));
                    merged.ai_source_hint = {
                        part_number: ctx.identity?.part_number,
                        material: ctx.identity?.material,
                        geometry: g,
                        blank: b,
                        features: ctx.features,
                    };
                }
                setForm(merged);
                setLoading(false);
            }).catch((e) => { setError(formatApiError(e)); setLoading(false); });
        }
    }, [id, isEdit]);

    // Helpers
    const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
    const selectedGrade = useMemo(() => grades.find((g) => g.id === form.steel_grade_id), [grades, form.steel_grade_id]);
    const selectedScrap = useMemo(() => scraps.find((s) => s.id === form.scrap_rate_id), [scraps, form.scrap_rate_id]);
    const selectedCustomer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);
    const plantMachines = useMemo(
        () => machines.filter((m) => m.plant_id === form.plant_id && m.is_active),
        [machines, form.plant_id],
    );
    const machineRateFor = useCallback(
        (machineId) => approvedRates.find((r) => r.machine_id === machineId)?.calculated_rate || null,
        [approvedRates],
    );

    // Override wins over the master-picker when > 0 (matches backend
    // _resolve_rates_for_zbc priority).
    const rmOverride = parseFloat(form.rm_rate_override) || 0;
    const scrapOverride = parseFloat(form.scrap_rate_override) || 0;
    const gradeRate = rmOverride > 0 ? rmOverride : (selectedGrade?.rate_per_kg || 0);
    const scrapRate = scrapOverride > 0 ? scrapOverride : (selectedScrap?.rate_per_kg || 0);

    const computed = useMemo(
        () => computeZbcLive(form, gradeRate, scrapRate, processRates),
        [form, gradeRate, scrapRate, processRates],
    );
    const variance = useMemo(
        () => computeVariance(computed.should_cost, form.supplier_quote),
        [computed.should_cost, form.supplier_quote],
    );

    // (Legacy manual "Calculate from dimensions" buttons have been removed;
    // Input Weight now auto-fills reactively as dimensions are entered.)

    // ---------------------------------------------------------------
    // AUTO-CALCULATION LAYER (reactive)
    //
    // Every derivation below is REACTIVE: it recomputes whenever its inputs
    // change. To prevent clobbering a user's manual entry, each auto-suggested
    // field is tracked in `manuallyEdited`; once the user types their own
    // value, the auto-calc pauses for that field until the user clicks "Reset"
    // on the sparkles tooltip.
    //
    // The `tips` object exposes derivation strings for the tooltip UI.
    // ---------------------------------------------------------------
    const [manuallyEdited, setManuallyEdited] = useState({});
    const markEdited = useCallback((k) => setManuallyEdited((m) => ({ ...m, [k]: true })), []);
    const resetAuto = useCallback((k) => setManuallyEdited((m) => { const n = { ...m }; delete n[k]; return n; }), []);

    // Clone-from-existing-ZBC: lets a Manual ZBC start from a previously
    // saved part's dimensions/route instead of a blank form, reusing the
    // same search endpoint the AI Wizard's "Load from Catalog" tab already
    // uses. Only offered for brand-new ZBCs, not while editing one.
    const [cloneOpen, setCloneOpen] = useState(false);
    const [cloneQuery, setCloneQuery] = useState("");
    const [cloneResults, setCloneResults] = useState([]);
    const [cloneLoading, setCloneLoading] = useState(false);
    const [cloneApplying, setCloneApplying] = useState(false);

    const searchClone = async () => {
        setCloneLoading(true);
        try {
            const { data } = await api.get(`/zbc/ai/catalog?q=${encodeURIComponent(cloneQuery)}&limit=25`);
            setCloneResults(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCloneLoading(false);
        }
    };

    const applyClone = async (zbcId) => {
        setCloneApplying(true);
        try {
            const { data: src } = await api.get(`/zbcs/${zbcId}`);
            setForm({
                ...emptyForm,
                ...src,
                id: undefined,
                part_number: "", part_name: "", navision_code: "", drawing_number: "",
                status: "DRAFT",
                supplier_id: "", supplier_name: "", supplier_quote: "", quote_date: "",
                recalc_pending: false, recalc_reason: null,
            });
            setManuallyEdited({});
            setCloneOpen(false);
            toast.success(`Cloned from ${src.part_number}`, { description: "Enter a new Part Number and Part Name, then review dimensions before saving." });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCloneApplying(false);
        }
    };

    // Auto-calc coefficient overrides from the Formula Registry (`/api/formulas`).
    // Falls back to DEFAULT_AUTOCALC_CFG if the endpoint is unavailable.
    const [autoCalcCfg, setAutoCalcCfg] = useState(DEFAULT_AUTOCALC_CFG);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/formulas");
                if (!cancelled) setAutoCalcCfg(buildAutoCalcConfig(r?.data || {}));
            } catch { /* first-run / no overrides, keep defaults */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Density from grade (drives all weight formulas)
    const densityTip = useMemo(
        () => deriveDensity(selectedGrade?.code, autoCalcCfg),
        [selectedGrade?.code, autoCalcCfg],
    );
    const density = densityTip.value;

    // Scrap allowance percentages seeded in SCRAP_RATES (unit === PCT)
    const scrapAllowances = useMemo(
        () => extractScrapAllowances(scraps, form.cutting_process),
        [scraps, form.cutting_process],
    );

    // A1 · Reactive Input Weight (sheet / bar / tube)
    const inputWeightAuto = useMemo(() => {
        if (form.rm_category === "SHEET_STEEL") {
            return sheetInputWeightWithOffal(form.blank_width_mm, form.blank_length_mm, form.thickness_mm, density, null, autoCalcCfg);
        }
        if (form.rm_category === "ROUND_BAR") {
            return roundBarInputWeightDetail(form.bar_od_mm, form.part_length_mm, density);
        }
        if (form.rm_category === "SQUARE_TUBE") {
            return squareTubeInputWeight(form.tube_size, form.tube_length_mm, form.thickness_mm, density);
        }
        return null;
    }, [
        form.rm_category, form.blank_width_mm, form.blank_length_mm, form.thickness_mm,
        form.bar_od_mm, form.part_length_mm,
        form.tube_size, form.tube_length_mm,
        density, autoCalcCfg,
    ]);

    // Which dimension fields are still missing? Used by the UI to show a
    // "waiting for…" hint next to Input Weight so users know why auto-calc
    // hasn't fired yet. Only fields relevant to the selected rm_category.
    const missingDims = useMemo(() => {
        const num = (v) => (parseFloat(v) || 0) > 0;
        if (form.rm_category === "SHEET_STEEL") {
            const m = [];
            if (!num(form.blank_width_mm)) m.push("Blank Width");
            if (!num(form.blank_length_mm)) m.push("Blank Length");
            if (!num(form.thickness_mm)) m.push("Thickness");
            return m;
        }
        if (form.rm_category === "ROUND_BAR") {
            const m = [];
            if (!num(form.bar_od_mm)) m.push("Bar OD");
            if (!num(form.part_length_mm)) m.push("Part Length");
            return m;
        }
        if (form.rm_category === "SQUARE_TUBE") {
            const m = [];
            if (!form.tube_size) m.push("Tube Size");
            if (!num(form.tube_length_mm)) m.push("Tube Length");
            if (!num(form.thickness_mm)) m.push("Thickness");
            return m;
        }
        return [];
    }, [form.rm_category, form.blank_width_mm, form.blank_length_mm, form.thickness_mm, form.bar_od_mm, form.part_length_mm, form.tube_size, form.tube_length_mm]);

    // A2 · Reactive Finish Weight from Input Weight + scrap allowances
    // GAP 1: For machined parts (round bar, turning/facing/boring/drilling ops)
    // auto-derivation is disabled; user must enter Finish Weight from drawing.
    const machinedPart = useMemo(
        () => isMachinedPart(form.rm_category, form.operations),
        [form.rm_category, form.operations],
    );
    const finishWeightAuto = useMemo(
        () => deriveFinishWeight(form.input_weight_kg, scrapAllowances, {
            isMachinedPart: machinedPart,
            rmCategory: form.rm_category,
            operations: form.operations,
            roundBarMachiningLossPct: autoCalcCfg.round_bar_machining_loss_pct,
            prismaticMachiningLossPct: autoCalcCfg.prismatic_machining_loss_pct,
            pipeMachiningLossPct: autoCalcCfg.pipe_machining_loss_pct,
        }),
        [form.input_weight_kg, scrapAllowances, machinedPart, form.rm_category, form.operations, autoCalcCfg],
    );

    // A3 · Auto-suggest scrap type from grade
    const scrapAuto = useMemo(() => deriveScrapCodeFromGrade(selectedGrade), [selectedGrade]);

    // A4 · Auto-suggest RM category from grade
    const rmCategoryAuto = useMemo(() => deriveRmCategoryFromGrade(selectedGrade), [selectedGrade]);

    // A6 · Cut perimeter from blank dimensions
    const cutPerimeterAuto = useMemo(
        () => (form.rm_category === "SHEET_STEEL"
            ? deriveCutPerimeter(form.blank_width_mm, form.blank_length_mm)
            : null),
        [form.rm_category, form.blank_width_mm, form.blank_length_mm],
    );

    // B1 · Cutting process from thickness
    const cuttingProcessAuto = useMemo(
        () => deriveCuttingProcess(form.thickness_mm, autoCalcCfg),
        [form.thickness_mm, autoCalcCfg],
    );

    // Effects: apply auto values unless user has edited that field
    useEffect(() => {
        if (!inputWeightAuto || manuallyEdited.input_weight_kg) return;
        if (parseFloat(form.input_weight_kg) === inputWeightAuto.value) return;
        setForm((f) => ({ ...f, input_weight_kg: inputWeightAuto.value }));
    }, [inputWeightAuto, manuallyEdited.input_weight_kg]); 

    useEffect(() => {
        if (!finishWeightAuto || manuallyEdited.finish_weight_kg) return;
        if (parseFloat(form.finish_weight_kg) === finishWeightAuto.value) return;
        setForm((f) => ({ ...f, finish_weight_kg: finishWeightAuto.value }));
    }, [finishWeightAuto, manuallyEdited.finish_weight_kg]); 

    useEffect(() => {
        if (!scrapAuto || manuallyEdited.scrap_rate_id) return;
        const target = scraps.find((s) => s.code === scrapAuto.value);
        if (!target || form.scrap_rate_id === target.id) return;
        setForm((f) => ({ ...f, scrap_rate_id: target.id }));
    }, [scrapAuto, scraps, manuallyEdited.scrap_rate_id]); 

    useEffect(() => {
        if (!rmCategoryAuto || manuallyEdited.rm_category) return;
        if (form.rm_category === rmCategoryAuto.value) return;
        setForm((f) => ({ ...f, rm_category: rmCategoryAuto.value }));
    }, [rmCategoryAuto, manuallyEdited.rm_category]); 

    useEffect(() => {
        if (!cutPerimeterAuto || manuallyEdited.cut_perimeter_mtr) return;
        if (parseFloat(form.cut_perimeter_mtr) === cutPerimeterAuto.value) return;
        setForm((f) => ({ ...f, cut_perimeter_mtr: cutPerimeterAuto.value }));
    }, [cutPerimeterAuto, manuallyEdited.cut_perimeter_mtr]); 

    useEffect(() => {
        if (!cuttingProcessAuto || manuallyEdited.cutting_process) return;
        if (form.cutting_process === cuttingProcessAuto.value) return;
        setForm((f) => ({ ...f, cutting_process: cuttingProcessAuto.value }));
    }, [cuttingProcessAuto, manuallyEdited.cutting_process]); 

    // Wrap setF so any user edit auto-flags the field as manually edited
    const setFWithEdit = useCallback((patch) => {
        const keys = Object.keys(patch);
        if (keys.length) setManuallyEdited((m) => {
            const next = { ...m };
            for (const k of keys) next[k] = true;
            return next;
        });
        setF(patch);
    }, [setF]);

    const autoTips = {
        input_weight_kg: inputWeightAuto?.reason,
        finish_weight_kg: finishWeightAuto?.reason,
        scrap_rate_id: scrapAuto?.reason,
        rm_category: rmCategoryAuto?.reason,
        cut_perimeter_mtr: cutPerimeterAuto?.reason,
        cutting_process: cuttingProcessAuto?.reason,
        density: densityTip?.reason,
        // UI hint: which dims are still missing (empty array when auto-calc has fired)
        _missing_dims: missingDims,
    };

    // Operations
    const addOp = () => setF({ operations: [...(form.operations || []), { name: "", uom: "PER_HOUR", qty_or_time: 0, rate: 0, cost: 0 }] });
    const updateOp = (idx, patch) => {
        const ops = [...form.operations];
        ops[idx] = { ...ops[idx], ...patch };
        // B5 · Auto-pick machine on operation name change if user has not
        // manually picked one for this row.
        if (patch.name && !ops[idx].machine_id && !ops[idx]._machine_manually_set) {
            const hit = deriveMachineFromOpName(patch.name, plantMachines);
            if (hit) {
                ops[idx].machine_id = hit.value;
                ops[idx].machine_code = hit.machine_code;
                ops[idx]._machine_auto_reason = hit.reason;
                const r = machineRateFor(hit.value);
                if (r) ops[idx].rate = r;
            }
        }
        if (patch.machine_id !== undefined) {
            const m = plantMachines.find((x) => x.id === patch.machine_id);
            const r = machineRateFor(patch.machine_id);
            ops[idx].machine_code = m?.code || "";
            if (r) ops[idx].rate = r;
            ops[idx]._machine_manually_set = true;
            ops[idx]._machine_auto_reason = null;
        }
        ops[idx].cost = computeOpCost(ops[idx]);
        setF({ operations: ops });
    };
    const removeOp = (idx) => setF({ operations: form.operations.filter((_, i) => i !== idx) });

    // Weld joints
    const addJoint = () => {
        // B4 · Auto-suggest fillet from thickness (structural rule of thumb)
        const fillet = deriveFilletSize(form.thickness_mm, autoCalcCfg);
        const newJoint = {
            description: "",
            length_mtr: 0,
            fillet_mm: fillet ? fillet.value : 0,
            qty: 1,
            _fillet_auto_reason: fillet ? fillet.reason : null,
        };
        setF({ weld_joints: [...(form.weld_joints || []), newJoint] });
    };
    const updateJoint = (idx, patch) => {
        const js = [...form.weld_joints];
        js[idx] = { ...js[idx], ...patch };
        // Once user overrides fillet, clear the auto-reason marker
        if (patch.fillet_mm !== undefined) js[idx]._fillet_manually_set = true;
        setF({ weld_joints: js });
    };
    const removeJoint = (idx) => setF({ weld_joints: form.weld_joints.filter((_, i) => i !== idx) });

    // Filling material components (Section 2B)
    const addFillingComponent = () =>
        setF({ filling_components: [...(form.filling_components || []), { name: "", qty: "", rate: "" }] });
    const updateFillingComponent = (idx, patch) => {
        const fcs = [...(form.filling_components || [])];
        fcs[idx] = { ...fcs[idx], ...patch };
        setF({ filling_components: fcs });
    };
    const removeFillingComponent = (idx) =>
        setF({ filling_components: form.filling_components.filter((_, i) => i !== idx) });

    // Save
    const buildPayload = () => {
        const payload = { ...form };
        // Coerce numbers
        const numFields = ["qty_per_bom", "blank_width_mm", "blank_length_mm", "thickness_mm",
            "bar_od_mm", "bar_length_mm", "part_od_mm", "part_id_mm", "part_length_mm",
            "tube_length_mm", "input_weight_kg", "finish_weight_kg", "bop_price",
            "cut_perimeter_mtr", "supplier_quote",
            "rm_lump_cost", "shell_volume_cum", "pouring_density_kg_cum",
            "rm_rate_override", "scrap_rate_override",
            "tooling_cost_rs", "annual_volume", "amortization_years"];
        for (const k of numFields) {
            if (payload[k] === "" || payload[k] == null) payload[k] = null;
            else payload[k] = parseFloat(payload[k]);
        }
        // Integer coercion for fields backend types as int
        for (const k of ["annual_volume", "amortization_years"]) {
            if (payload[k] != null && !Number.isNaN(payload[k])) payload[k] = Math.trunc(payload[k]);
        }
        if (payload.qty_per_bom == null) payload.qty_per_bom = 1;
        // Clean operations
        payload.operations = (payload.operations || []).map((o) => ({
            ...o,
            qty_or_time: parseFloat(o.qty_or_time) || 0,
            rate: parseFloat(o.rate) || 0,
            parts_per_box: o.parts_per_box ? parseFloat(o.parts_per_box) : null,
            cost: computeOpCost(o),
        }));
        // Clean joints
        payload.weld_joints = (payload.weld_joints || []).map((j) => ({
            ...j,
            length_mtr: parseFloat(j.length_mtr) || 0,
            fillet_mm: parseFloat(j.fillet_mm) || 0,
            qty: parseInt(j.qty, 10) || 1,
        }));
        // Clean filling components (Section 2B)
        payload.filling_components = (payload.filling_components || []).map((fc) => ({
            name: fc.name || "",
            qty: parseFloat(fc.qty) || 0,
            rate: parseFloat(fc.rate) || 0,
            unit: "kg",
        }));
        if (payload.quote_date && typeof payload.quote_date === "string" && payload.quote_date.length === 10) {
            payload.quote_date = `${payload.quote_date}T00:00:00Z`;
        }
        return payload;
    };

    const save = async (newStatus) => {
        if (!form.part_number || !form.part_name || !form.customer_id || !form.plant_id) {
            toast.error("Part identity is incomplete: fill Section 1 first");
            setStep(0);
            return;
        }
        // Sprint 4 · Option C: save is never blocked on Finish Weight.
        // The auto-derive layer always fills a value; confidence chip shows source.
        setSubmitting(true);
        try {
            const payload = buildPayload();
            if (newStatus) payload.status = newStatus;
            const { data } = isEdit
                ? await api.put(`/zbcs/${id}`, payload)
                : await api.post("/zbcs", payload);
            toast.success(isEdit ? "ZBC updated" : "ZBC created", { description: `Should-Cost: ${fmtINR(data.should_cost || 0)}` });
            navigate(`/zbc/${data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    if (loading) return <LoadingState />;
    if (error) return <ErrorState message={error} />;

    const isBOP = form.rm_category === "BOP_PURCHASED";

    return (
        <div data-testid="zbc-form-page">
            {!isEdit && (
                <DuplicateCheckBanner
                    partNumber={form.part_number}
                    customerId={form.customer_id}
                    onContinue={() => setF({ _allowDup: true })}
                />
            )}
            <PageHeader
                eyebrow={isEdit ? "EDIT ZBC" : "NEW ZBC"}
                title={isEdit ? `Edit · ${form.part_number || "ZBC"}` : "New Should-Cost Analysis"}
                subtitle="Build the part's cost from scratch: material, machines, cutting, welding, then compare against the supplier's quote."
                actions={
                    <div className="flex items-center gap-2">
                        <SlateButton variant="outline" onClick={() => navigate("/zbc")}>Cancel</SlateButton>
                        <SlateButton onClick={() => save("DRAFT")} disabled={submitting} variant="outline">Save Draft</SlateButton>
                        {canSelfVerify ? (
                            <CopperButton onClick={() => save("VERIFIED")} disabled={submitting} data-testid="zbc-save-verified">
                                <Check className="h-4 w-4" /> {submitting ? "Saving…" : "Mark as Verified"}
                            </CopperButton>
                        ) : (
                            <CopperButton
                                onClick={() => save("DRAFT")}
                                disabled={submitting}
                                title="Save as Draft, then a different reviewer verifies it from the ZBC detail page"
                            >
                                {submitting ? "Saving…" : "Save"}
                            </CopperButton>
                        )}
                    </div>
                }
            />

            <div className="grid grid-cols-12 gap-5">
                {/* Left progress stepper */}
                <aside className="col-span-12 lg:col-span-2">
                    <Card className="p-3 sticky top-20">
                        <div className="eyebrow text-slate-500 mb-3 px-1">PROGRESS</div>
                        <nav className="space-y-1">
                            {SECTIONS.map((s, i) => {
                                const Icon = s.icon;
                                const active = step === i;
                                const done = i < step;
                                return (
                                    <button
                                        key={s.key}
                                        onClick={() => setStep(i)}
                                        data-testid={`zbc-step-${s.key}`}
                                        className={[
                                            "nav-active-indicator w-full flex items-center gap-2.5 h-9 px-2 mx-1 rounded-md text-[12px] transition-colors",
                                            active ? "is-active bg-[hsl(var(--copper-soft))] text-slate-900 font-medium" : "text-slate-600 hover:bg-slate-100",
                                            done && !active && "text-emerald-700",
                                        ].filter(Boolean).join(" ")}
                                    >
                                        <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${
                                            done ? "bg-emerald-500 text-white" :
                                            active ? "bg-[hsl(var(--copper))] text-white" :
                                            "bg-slate-100 text-slate-400"
                                        }`}>{done ? "✓" : i + 1}</span>
                                        <span className="truncate">{s.label}</span>
                                    </button>
                                );
                            })}
                        </nav>
                    </Card>
                </aside>

                {/* Center form */}
                <section className="col-span-12 lg:col-span-7">
                    {step === 0 && <SectionIdentity form={form} setF={setF} customers={customers} plants={plants} selectedCustomer={selectedCustomer} autoTips={autoTips} manuallyEdited={manuallyEdited} resetAuto={resetAuto} markEdited={markEdited} isEdit={isEdit} onOpenClone={() => setCloneOpen(true)} />}
                    {step === 1 && !isBOP && (
                        <SectionMaterial
                            form={form} setF={setF} grades={grades} scraps={scraps}
                            selectedGrade={selectedGrade} selectedScrap={selectedScrap}
                            computed={computed}
                            addFillingComponent={addFillingComponent}
                            updateFillingComponent={updateFillingComponent}
                            removeFillingComponent={removeFillingComponent}
                            autoTips={autoTips}
                            manuallyEdited={manuallyEdited}
                            resetAuto={resetAuto}
                            markEdited={markEdited}
                            finishWeightAuto={finishWeightAuto}
                        />
                    )}
                    {step === 1 && isBOP && (
                        <Card className="p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <FileText className="h-5 w-5 text-[hsl(var(--copper))] mt-0.5" />
                                <div>
                                    <h2 className="font-display text-xl font-semibold">Bought-Out Part</h2>
                                    <p className="text-sm text-slate-500">Sections 2–5 are disabled. Just enter the supplier price.</p>
                                </div>
                            </div>
                            <Field label="BOP Price (₹)" required>
                                <NumberInput value={form.bop_price} onChange={(e) => setF({ bop_price: e.target.value })} step="0.01" />
                            </Field>
                        </Card>
                    )}
                    {step === 2 && !isBOP && (
                        <SectionConversion
                            form={form} setF={setF} machines={plantMachines}
                            machineRateFor={machineRateFor}
                            conversionOps={conversionOps}
                            addOp={addOp} updateOp={updateOp} removeOp={removeOp}
                            computed={computed}
                        />
                    )}
                    {step === 3 && !isBOP && <SectionCutting form={form} setF={setF} processRates={processRates} computed={computed} autoTips={autoTips} manuallyEdited={manuallyEdited} resetAuto={resetAuto} />}
                    {step === 4 && !isBOP && <SectionWelding form={form} setF={setF} processRates={processRates} addJoint={addJoint} updateJoint={updateJoint} removeJoint={removeJoint} computed={computed} />}
                    {step === 5 && <SectionResult form={form} setF={setF} computed={computed} variance={variance} selectedCustomer={selectedCustomer} onSaveDraft={() => save("DRAFT")} onVerify={() => save("VERIFIED")} submitting={submitting} />}

                    {/* Skip-to-result for BOP */}
                    {isBOP && step > 1 && step < 5 && (
                        <Card className="p-6 text-center">
                            <p className="text-sm text-slate-500 mb-3">This section doesn&apos;t apply to bought-out parts. Skip to Section 6.</p>
                            <CopperButton onClick={() => setStep(5)}>Jump to Should-Cost <ArrowRight className="h-4 w-4" /></CopperButton>
                        </Card>
                    )}

                    {/* Step nav */}
                    <div className="flex items-center justify-between mt-5">
                        <SlateButton variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
                            <ArrowLeft className="h-4 w-4" /> Back
                        </SlateButton>
                        <span className="text-[12px] text-slate-500">Step {step + 1} of {SECTIONS.length}</span>
                        <CopperButton onClick={() => setStep(Math.min(SECTIONS.length - 1, step + 1))} disabled={step === SECTIONS.length - 1}>
                            Next <ArrowRight className="h-4 w-4" />
                        </CopperButton>
                    </div>
                </section>

                {/* Right running totals */}
                <aside className="col-span-12 lg:col-span-3">
                    <Card className="p-4 sticky top-20">
                        <div className="eyebrow text-slate-500 mb-3">RUNNING TOTALS</div>
                        <CostRow label={<>Net Material<FormulaInfo formulaId="should_cost" /></>} value={computed.net_material_cost} />
                        {computed.total_filling_cost_rs > 0 && (
                            <CostRow label="Filling Material" value={computed.total_filling_cost_rs} />
                        )}
                        <CostRow label="Conversion" value={computed.conversion_cost} />
                        <CostRow label={<>Cutting<FormulaInfo formulaId="cutting_cost" /></>} value={computed.cutting_cost} />
                        <CostRow label={<>Welding<FormulaInfo formulaId="welding_cost" /></>} value={computed.welding_cost} />
                        <div className="border-t border-slate-200 mt-2 pt-2">
                            <CostRow label="Base Cost" value={computed.base_cost} bold />
                            <CostRow label={<>+ Allowances<FormulaInfo formulaId="zbc_icc_pct" /></>} value={computed.allowance_total} muted />
                        </div>
                        <div className="border-t border-slate-200 mt-2 pt-3 bg-[hsl(var(--copper-soft))] -mx-4 -mb-4 px-4 pb-4 rounded-b-lg">
                            <div className="eyebrow text-slate-600">SHOULD-COST<FormulaInfo formulaId="should_cost" /></div>
                            <div className="font-display text-2xl font-semibold text-[hsl(var(--copper))] font-mono tabular-nums animate-calc-pulse" key={computed.should_cost}>
                                {fmtINR(computed.should_cost)}
                            </div>
                            {variance.badge && (
                                <div className="mt-2 flex items-center gap-2">
                                    <VarianceBadge badge={variance.badge} pct={variance.pct} />
                                    {variance.amount != null && (
                                        <span className="text-[11px] font-mono text-slate-600">{variance.amount >= 0 ? "+" : ""}{fmtINR(variance.amount)}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </Card>
                </aside>
            </div>

            <CloneModal
                open={cloneOpen}
                onClose={() => setCloneOpen(false)}
                query={cloneQuery}
                setQuery={setCloneQuery}
                results={cloneResults}
                loading={cloneLoading}
                applying={cloneApplying}
                onSearch={searchClone}
                onPick={applyClone}
            />
        </div>
    );
}

function CloneModal({ open, onClose, query, setQuery, results, loading, applying, onSearch, onPick }) {
    return (
        <Modal open={open} onClose={onClose} title="Clone from Existing ZBC" size="lg">
            <div className="space-y-3">
                <p className="text-[12.5px] text-slate-600">
                    Search your saved ZBCs by part number, part name, or drawing number. Picking one copies its
                    dimensions, material, conversion route, cutting and welding as a starting template; you still
                    enter a new Part Number and Part Name before saving.
                </p>
                <div className="flex gap-2">
                    <TextInput
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
                        placeholder="Search part number, name, or drawing number…"
                        data-testid="clone-search-input"
                    />
                    <CopperButton onClick={onSearch} disabled={loading} data-testid="clone-search-btn">
                        <Search className="h-4 w-4" /> {loading ? "Searching…" : "Search"}
                    </CopperButton>
                </div>
                <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200">
                    {results.length === 0 && (
                        <div className="text-center py-8 text-[13px] text-slate-400">
                            {loading ? "Searching…" : "No results yet. Search above."}
                        </div>
                    )}
                    {results.length > 0 && (
                        <table className="w-full text-[12px]">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60 sticky top-0">
                                    <th className="text-left py-2 px-3">Part Number</th>
                                    <th className="text-left py-2 px-3">Part Name</th>
                                    <th className="text-right py-2 px-3">Should-Cost</th>
                                    <th className="text-right py-2 px-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r) => (
                                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`clone-row-${r.id}`}>
                                        <td className="py-1.5 px-3 font-mono font-semibold">{r.part_number}</td>
                                        <td className="py-1.5 px-3">{r.part_name}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums">{fmtINR(r.should_cost || 0)}</td>
                                        <td className="py-1.5 px-3 text-right">
                                            <SlateButton
                                                onClick={() => onPick(r.id)}
                                                disabled={applying}
                                                className="h-7 px-2.5 text-[11px]"
                                                data-testid={`clone-use-${r.id}`}
                                            >
                                                {applying ? "Loading…" : "Use as Template"}
                                            </SlateButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </Modal>
    );
}

function CostRow({ label, value, bold, muted }) {
    return (
        <div className="flex items-baseline justify-between py-1">
            <span className={`text-[12px] ${muted ? "text-slate-400" : "text-slate-600"}`}>{label}</span>
            <span className={`font-mono tabular-nums text-[13px] ${bold ? "font-semibold text-slate-900" : muted ? "text-slate-400" : "text-slate-700"}`}>
                {fmtINR(value || 0)}
            </span>
        </div>
    );
}

function InfoBox({ children }) {
    return (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 flex items-start gap-2 mb-4">
            <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-[12px] text-blue-900 leading-snug">{children}</div>
        </div>
    );
}

// ---------------- Section 1: Identity ----------------
function SectionIdentity({ form, setF, customers, plants, selectedCustomer, autoTips = {}, manuallyEdited = {}, resetAuto, isEdit, onOpenClone }) {
    const rmOverridden = !!manuallyEdited.rm_category;
    return (
        <Card className="p-6">
            <div className="flex items-start justify-between gap-3 mb-1">
                <h2 className="font-display text-xl font-semibold">1 · Part Identity</h2>
                {!isEdit && (
                    <SlateButton variant="outline" onClick={onOpenClone} className="h-8 text-[12px] shrink-0" data-testid="clone-from-existing-btn">
                        <Copy className="h-3.5 w-3.5" /> Clone from Existing ZBC
                    </SlateButton>
                )}
            </div>
            <p className="text-[12px] text-slate-500 mb-5">Tell us what this part is, who it&apos;s for, and where it&apos;s made.</p>

            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Part Number" required helper="Unique per customer">
                        <TextInput value={form.part_number} onChange={(e) => setF({ part_number: e.target.value })} placeholder="e.g. 1414C03901" />
                    </Field>
                    <Field label="Navision Code" helper="Internal ERP code">
                        <TextInput value={form.navision_code} onChange={(e) => setF({ navision_code: e.target.value })} placeholder="e.g. B69060000016" />
                    </Field>
                </div>
                <Field label="Part Name" required>
                    <TextInput value={form.part_name} onChange={(e) => setF({ part_name: e.target.value })} placeholder="e.g. Axle Pin Boss" maxLength={200} />
                </Field>
                <Field label="Drawing Number" helper="From engineering drawing, optional">
                    <TextInput value={form.drawing_number} onChange={(e) => setF({ drawing_number: e.target.value })} />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                    <Field label="Customer" required>
                        <Select value={form.customer_id} onChange={(e) => setF({ customer_id: e.target.value })}>
                            <option value="">Select customer…</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                        </Select>
                        {selectedCustomer && (
                            <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1">
                                <Sparkles className="h-3 w-3 text-[hsl(var(--copper))]" />
                                Formula variant: <span className="font-semibold text-slate-700">{selectedCustomer.formula_variant}</span>
                            </div>
                        )}
                    </Field>
                    <Field label="Plant" required>
                        <Select value={form.plant_id} onChange={(e) => setF({ plant_id: e.target.value })}>
                            <option value="">Select plant…</option>
                            {plants.filter((p) => p.is_active).map((p) => <option key={p.id} value={p.id}>{p.code} · {p.city}</option>)}
                        </Select>
                    </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Field label="Qty per BOM" required>
                        <NumberInput value={form.qty_per_bom} onChange={(e) => setF({ qty_per_bom: e.target.value })} min="1" step="1" />
                    </Field>
                    <Field
                        label={<>RM Category {autoTips.rm_category && <AutoTip reason={autoTips.rm_category} overridden={rmOverridden} onReset={() => resetAuto?.("rm_category")} />}</>}
                        required
                    >
                        <Select value={form.rm_category} onChange={(e) => setF({ rm_category: e.target.value })}>
                            {RM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Select>
                        <div className="text-[11px] text-slate-500 mt-1.5">{RM_OPTIONS.find((o) => o.value === form.rm_category)?.desc}</div>
                    </Field>
                </div>

                <Field label="Part Use / Description">
                    <textarea
                        value={form.part_use}
                        onChange={(e) => setF({ part_use: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
                        placeholder="e.g. Front axle pin, connects steering knuckle to beam"
                    />
                </Field>
                <Field label="Notes">
                    <textarea
                        value={form.notes}
                        onChange={(e) => setF({ notes: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
                        placeholder="Any costing notes or assumptions"
                    />
                </Field>
            </div>
        </Card>
    );
}

// ---------------- Section 2: Material ----------------
function SectionMaterial({ form, setF, grades, scraps, selectedGrade, selectedScrap, computed, addFillingComponent, updateFillingComponent, removeFillingComponent, autoTips = {}, manuallyEdited = {}, resetAuto, finishWeightAuto }) {
    const rm = form.rm_category;
    const isLump = form.rm_entry_mode === "LUMP";
    const fillingOn = !!form.filling_material_enabled;
    const isVolvoCW = form.part_number === "14784565";
    // GAP 1: mirror the machined-part detector so the banner + red ring
    // work inside this child component too.
    const machinedPart = isMachinedPart(form.rm_category, form.operations);

    // Cache filling-materials master list (for the dropdown + rate auto-fill)
    const [fillingMaster, setFillingMaster] = React.useState([]);
    React.useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await api.get("/filling-materials");
                if (!cancel) setFillingMaster(Array.isArray(r?.data) ? r.data : []);
            } catch (e) { /* ignore, dropdown still works as free text */ }
        })();
        return () => { cancel = true; };
    }, []);

    const totalFillWeight = (form.filling_components || []).reduce((s, fc) => s + (parseFloat(fc.qty) || 0), 0);
    const totalFillCost = (form.filling_components || []).reduce(
        (s, fc) => s + (parseFloat(fc.qty) || 0) * (parseFloat(fc.rate) || 0), 0,
    );
    const calculatedFillMass =
        form.shell_volume_cum && form.pouring_density_kg_cum
            ? parseFloat(form.shell_volume_cum) * parseFloat(form.pouring_density_kg_cum)
            : null;

    const loadVolvoCWFillingData = () => {
        const volvoGrade = grades.find((g) => (g.customer_tag === "Volvo CE India") && (g.name || "").includes("VSS235"));
        setF({
            rm_entry_mode: "LUMP",
            rm_lump_cost: 930376,
            rm_lump_note: "From COST-BOM W44 SUMPRODUCT. Sheet uses qty-squared sum as RM figure.",
            steel_grade_id: volvoGrade ? volvoGrade.id : form.steel_grade_id,
            filling_material_enabled: true,
            shell_volume_cum: 1.072,
            pouring_density_kg_cum: 3080,
            filling_components: [
                { name: "DEMO_Steel Scrap (fill)",  qty: 234,  rate: 23 },
                { name: "DEMO_Hematite Lumps",      qty: 234,  rate: 23 },
                { name: "DEMO_Hematite Fine",       qty: 234,  rate: 23 },
                { name: "DEMO_Chemical (Complast)", qty: 23,   rate: 23 },
                { name: "DEMO_Cement 53 grade",     qty: 4234, rate: 23 },
                { name: "DEMO_Water",               qty: 234,  rate: 123 },
            ],
        });
        toast.success("Loaded Volvo EC210E filling data");
    };

    return (
        <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-1">2 · Material Cost</h2>
            <p className="text-[12px] text-slate-500 mb-4">Pick the steel, enter the weight, and the system computes input cost minus scrap credit.</p>
            <InfoBox>
                <strong>How this works:</strong> enter the dimensions and select the grade. CostIQ picks up the current rate, calculates input weight × rate, deducts scrap credit (4% irrecoverable), and gives you the Net Material Cost.
            </InfoBox>

            {/* --- Entry-mode toggle (CALCULATE vs LUMP) --- */}
            {rm === "SHEET_STEEL" && (
                <div className="mt-4 mb-5 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="rm-entry-mode-toggle">
                    <div className="eyebrow text-slate-500 mb-2">ENTRY MODE</div>
                    <div className="flex flex-col gap-2 text-[13px]">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="rm_entry_mode" value="CALCULATE"
                                checked={!isLump}
                                onChange={() => setF({ rm_entry_mode: "CALCULATE" })}
                                data-testid="rm-mode-calculate" />
                            <span><b>Calculate</b> from weight × grade rate</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="rm_entry_mode" value="LUMP"
                                checked={isLump}
                                onChange={() => setF({ rm_entry_mode: "LUMP" })}
                                data-testid="rm-mode-lump" />
                            <span><b>Enter lump amount</b> directly (Volvo SUMPRODUCT / pre-computed RMC)</span>
                        </label>
                    </div>
                </div>
            )}

            {isVolvoCW && rm === "SHEET_STEEL" && (
                <div className="mb-5">
                    <CopperButton type="button" onClick={loadVolvoCWFillingData} data-testid="load-volvo-ec210e-data" className="text-sm">
                        <Plus className="h-3.5 w-3.5" /> Load Volvo EC210E Filling Data
                    </CopperButton>
                    <p className="text-[11px] text-slate-500 mt-1.5">Pre-fills lump RMC Rs 9,30,376 + 6 filling components totalling Rs 1,42,839.</p>
                </div>
            )}

            <div className="space-y-5">
                <div className="eyebrow text-slate-500">STEEL GRADE</div>
                <div className="grid grid-cols-2 gap-4">
                    <Field label={isLump ? "Steel Grade (optional)" : "Steel Grade"} required={!isLump}>
                        <Select value={form.steel_grade_id} onChange={(e) => setF({ steel_grade_id: e.target.value })}>
                            <option value="">Select grade…</option>
                            {grades.filter((g) => g.is_active).map((g) => <option key={g.id} value={g.id}>{g.code} · ₹{g.rate_per_kg}/kg</option>)}
                        </Select>
                        {selectedGrade && <div className="text-[11px] text-slate-500 mt-1.5">{selectedGrade.spec}</div>}
                    </Field>
                    <Field
                        label={<>Scrap Type {autoTips.scrap_rate_id && <AutoTip reason={autoTips.scrap_rate_id} overridden={!!manuallyEdited.scrap_rate_id} onReset={() => resetAuto?.("scrap_rate_id")} />}</>}
                        required={!isLump}
                    >
                        <Select value={form.scrap_rate_id} onChange={(e) => setF({ scrap_rate_id: e.target.value })}>
                            <option value="">Select scrap…</option>
                            {scraps.filter((s) => s.is_active).map((s) => <option key={s.id} value={s.id}>{s.code} · ₹{s.rate_per_kg}/kg</option>)}
                        </Select>
                    </Field>
                </div>

                {/* Optional per-ZBC rate overrides: when a user wants to type ₹/kg
                    directly without needing a matching master row. Value > 0 wins
                    over the dropdown selection above. */}
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Override RM Rate (₹/kg), optional">
                        <NumberInput
                            value={form.rm_rate_override ?? ""}
                            onChange={(e) => setF({ rm_rate_override: e.target.value })}
                            step="0.01"
                            placeholder={selectedGrade ? `Uses master ₹${selectedGrade.rate_per_kg}` : "Type a rate if no grade picked"}
                            data-testid="rm-rate-override"
                        />
                    </Field>
                    <Field label="Override Scrap Rate (₹/kg), optional">
                        <NumberInput
                            value={form.scrap_rate_override ?? ""}
                            onChange={(e) => setF({ scrap_rate_override: e.target.value })}
                            step="0.01"
                            placeholder={selectedScrap ? `Uses master ₹${selectedScrap.rate_per_kg}` : "Type a rate if no scrap picked"}
                            data-testid="scrap-rate-override"
                        />
                    </Field>
                </div>

                {isLump ? (
                    <>
                        <div className="eyebrow text-slate-500 pt-2">LUMP RM COST</div>
                        <Field label="Net Material Cost (Rs)" required>
                            <NumberInput value={form.rm_lump_cost}
                                onChange={(e) => setF({ rm_lump_cost: e.target.value })}
                                step="0.01" data-testid="rm-lump-cost-input" />
                        </Field>
                        <Field label="Note" helper="Where this lump amount comes from">
                            <TextInput value={form.rm_lump_note}
                                onChange={(e) => setF({ rm_lump_note: e.target.value })}
                                placeholder="e.g., From COST-BOM W44 SUMPRODUCT"
                                data-testid="rm-lump-note-input" />
                        </Field>
                    </>
                ) : (
                    <>
                        {rm === "SHEET_STEEL" && (
                            <>
                                <div className="eyebrow text-slate-500 pt-2">SHEET DIMENSIONS</div>
                                <div className="grid grid-cols-3 gap-4">
                                    <Field label="Blank Width (mm)" required>
                                        <NumberInput value={form.blank_width_mm} onChange={(e) => setF({ blank_width_mm: e.target.value })} step="0.1" />
                                    </Field>
                                    <Field label="Blank Length (mm)" required>
                                        <NumberInput value={form.blank_length_mm} onChange={(e) => setF({ blank_length_mm: e.target.value })} step="0.1" />
                                    </Field>
                                    <Field label="Thickness (mm)" required helper="Also used for cutting cost">
                                        <NumberInput value={form.thickness_mm} onChange={(e) => setF({ thickness_mm: e.target.value })} step="0.1" />
                                    </Field>
                                </div>
                                {(() => {
                                    // Sprint 3.5 · Detect round-bar-as-sheet mistake:
                                    // Very small blank length (≤ 2mm) combined with a "thickness" larger than
                                    // the length is a strong signal the user typed a cut length into "Thickness"
                                    // and a placeholder into "Blank Length": this is a round-bar part.
                                    const bl = parseFloat(form.blank_length_mm);
                                    const t = parseFloat(form.thickness_mm);
                                    const bw = parseFloat(form.blank_width_mm);
                                    const mistake = bl > 0 && t > 0 && bl <= 2 && t > bl && bw > 0;
                                    return mistake && (
                                        <div
                                            className="mt-1 flex items-start gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900"
                                            data-testid="round-bar-mistake-hint"
                                        >
                                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                                            <div>
                                                <div className="font-semibold">Are you sure this is a sheet-metal part?</div>
                                                <div className="text-amber-800/90 leading-snug mt-0.5">
                                                    Length ({bl}mm) &lt; Thickness ({t}mm) suggests this is actually a <span className="font-semibold">round bar</span> (Ø{bw} × {t}mm). Switch <span className="font-mono">RM Category</span> to <span className="font-mono">ROUND_BAR</span> for the correct cylinder formula and drawing-based Finish-Weight guard.
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </>
                        )}

                        {rm === "ROUND_BAR" && (
                            <>
                                <div className="eyebrow text-slate-500 pt-2">ROUND BAR DIMENSIONS</div>
                                <div className="grid grid-cols-3 gap-4">
                                    <Field label="Bar OD (mm)" required>
                                        <NumberInput value={form.bar_od_mm} onChange={(e) => setF({ bar_od_mm: e.target.value })} step="0.1" />
                                    </Field>
                                    <Field label="Bar Length (mm)" required helper="Standard 3000 / 6000">
                                        <NumberInput value={form.bar_length_mm} onChange={(e) => setF({ bar_length_mm: e.target.value })} step="1" />
                                    </Field>
                                    <Field label="Part Length (mm)" required>
                                        <NumberInput value={form.part_length_mm} onChange={(e) => setF({ part_length_mm: e.target.value })} step="0.1" />
                                    </Field>
                                </div>
                            </>
                        )}

                        {rm === "SQUARE_TUBE" && (
                            <div className="grid grid-cols-3 gap-4">
                                <Field label="Tube Size" required>
                                    <TextInput value={form.tube_size} onChange={(e) => setF({ tube_size: e.target.value })} placeholder="40×40×2" />
                                </Field>
                                <Field label="Tube Length (mm)" required>
                                    <NumberInput value={form.tube_length_mm} onChange={(e) => setF({ tube_length_mm: e.target.value })} step="1" />
                                </Field>
                                <Field label="Thickness (mm)" required>
                                    <NumberInput value={form.thickness_mm} onChange={(e) => setF({ thickness_mm: e.target.value })} step="0.1" />
                                </Field>
                            </div>
                        )}

                        {rm !== "FILL_MATERIAL" && (
                            <>
                                {autoTips._missing_dims && autoTips._missing_dims.length > 0 && (
                                    <div className="rounded-md border border-dashed border-[hsl(var(--copper))]/40 bg-[hsl(var(--copper-soft))]/40 px-3 py-2 text-[11.5px] text-slate-700 flex items-center gap-2" data-testid="waiting-for-dims">
                                        <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--copper))] shrink-0" />
                                        <span>
                                            <strong>Weight will auto-fill</strong> once you enter:&nbsp;
                                            <span className="font-mono text-[hsl(var(--copper))]">{autoTips._missing_dims.join(" · ")}</span>
                                        </span>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-4">
                                    <Field
                                        label={<>Input Weight (kg) {autoTips.input_weight_kg && <AutoTip reason={autoTips.input_weight_kg} overridden={!!manuallyEdited.input_weight_kg} onReset={() => resetAuto?.("input_weight_kg")} />}</>}
                                        required
                                    >
                                        <NumberInput
                                            value={form.input_weight_kg}
                                            onChange={(e) => setF({ input_weight_kg: e.target.value })}
                                            step="0.001"
                                            data-testid="input-weight-kg"
                                            className={autoTips.input_weight_kg && !manuallyEdited.input_weight_kg ? "ring-1 ring-[hsl(var(--copper))]/30 bg-[hsl(var(--copper-soft))]/20" : ""}
                                        />
                                    </Field>
                                    <Field
                                        label={<>Finish Weight (kg) {autoTips.finish_weight_kg && <AutoTip reason={autoTips.finish_weight_kg} overridden={!!manuallyEdited.finish_weight_kg} onReset={() => resetAuto?.("finish_weight_kg")} />}</>}
                                        required
                                    >
                                        <NumberInput
                                            value={form.finish_weight_kg}
                                            onChange={(e) => setF({ finish_weight_kg: e.target.value })}
                                            step="0.001"
                                            data-testid="finish-weight-kg"
                                            className={
                                                autoTips.finish_weight_kg && !manuallyEdited.finish_weight_kg
                                                    ? "ring-1 ring-[hsl(var(--copper))]/30 bg-[hsl(var(--copper-soft))]/20"
                                                    : ""
                                            }
                                        />
                                    </Field>
                                </div>
                                {finishWeightAuto && !manuallyEdited.finish_weight_kg && parseFloat(form.finish_weight_kg) > 0 && (() => {
                                    // Sprint 4 · Option C: confidence chip explaining the source
                                    const conf = finishWeightAuto.confidence;
                                    const isMed = conf === "MEDIUM";
                                    const isLow = conf === "LOW";
                                    const dot = isMed ? "bg-amber-500" : (isLow ? "bg-slate-400" : "bg-emerald-500");
                                    const label = isMed
                                        ? `Rule-derived · ${finishWeightAuto.shape} loss ${finishWeightAuto.lossPct}%`
                                        : `Sheet-metal blanking loss ${finishWeightAuto.lossPct}%`;
                                    return (
                                        <div
                                            className="mt-1 inline-flex items-center gap-1.5 text-[10.5px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5"
                                            data-testid="finish-weight-confidence-chip"
                                            title="You can override this value manually; it will not block saving."
                                        >
                                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
                                            <Sparkles className="w-3 h-3 text-[hsl(var(--copper))]" />
                                            <span>{label}</span>
                                        </div>
                                    );
                                })()}
                            </>
                        )}
                    </>
                )}

                {/* Live calc box (only when in CALCULATE mode AND a grade is picked) */}
                {!isLump && selectedGrade && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                        <div className="eyebrow text-slate-500 mb-2">LIVE CALCULATION</div>
                        <table className="w-full text-[12px]">
                            <tbody>
                                <tr><td className="text-slate-600">Input Weight</td><td className="text-right font-mono tabular-nums">{fmtIN(parseFloat(form.input_weight_kg) || 0, { decimals: 3 })} kg</td></tr>
                                <tr><td className="text-slate-600">× RM Rate</td><td className="text-right font-mono tabular-nums">{fmtINR(gradeRateOrZero(selectedGrade))}/kg</td></tr>
                                <tr className="border-t border-slate-200"><td className="text-slate-900 font-medium">Gross Material</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(computed.gross_material_cost)}</td></tr>
                                <tr><td className="text-slate-600 pt-1">− Scrap Credit (96% × scrap rate)<FormulaInfo formulaId="scrap_recoverable" /></td><td className="text-right font-mono tabular-nums text-emerald-600">−{fmtINR(computed.scrap_credit)}</td></tr>
                                <tr className="border-t-2 border-slate-300"><td className="text-slate-900 font-semibold pt-2">Net Material Cost<FormulaInfo formulaId="should_cost" /></td><td className="text-right font-mono tabular-nums font-bold text-[hsl(var(--copper))] pt-2">{fmtINR(computed.net_material_cost)}</td></tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Lump mode echo card */}
                {isLump && form.rm_lump_cost && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                        <div className="eyebrow text-slate-500 mb-2">LUMP RM (NO WEIGHT CALC)</div>
                        <table className="w-full text-[12px]">
                            <tbody>
                                <tr><td className="text-slate-900 font-semibold pt-2">Net Material Cost</td><td className="text-right font-mono tabular-nums font-bold text-[hsl(var(--copper))] pt-2">{fmtINR(parseFloat(form.rm_lump_cost) || 0)}</td></tr>
                            </tbody>
                        </table>
                        {form.rm_lump_note && <p className="text-[11px] text-slate-500 mt-2">{form.rm_lump_note}</p>}
                    </div>
                )}

                {/* ============ SECTION 2B · FILLING MATERIAL ============ */}
                {rm === "SHEET_STEEL" && (
                    <div className="pt-4 mt-2 border-t-2 border-dashed border-slate-200">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox"
                                checked={fillingOn}
                                onChange={(e) => setF({ filling_material_enabled: e.target.checked })}
                                data-testid="filling-material-toggle" />
                            <span className="text-[13px] font-semibold">This part contains filling material (counterweight)</span>
                        </label>

                        {fillingOn && (
                            <div className="mt-4 space-y-4" data-testid="section-2b-filling">
                                <h3 className="font-display text-lg font-semibold">2B · Filling Material Cost</h3>
                                <InfoBox>
                                    <strong>For counterweight parts</strong> where the steel shell is filled with a hematite-cement mix. Add each filling component separately. Selecting a material from the dropdown auto-fills and locks its rate from the master catalogue; pick &quot;Other (free text)&quot; to type a rate by hand.
                                </InfoBox>

                                <div className="flex justify-end">
                                    <SlateButton type="button" onClick={addFillingComponent} className="h-8 px-3 text-xs" data-testid="add-fill-component">
                                        <Plus className="h-3.5 w-3.5" /> Add Fill Component
                                    </SlateButton>
                                </div>

                                <div className="rounded-md border border-slate-200 overflow-hidden">
                                    <table className="w-full text-[12px]">
                                        <thead>
                                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                                <th className="eyebrow text-left py-2.5 px-3">Material</th>
                                                <th className="eyebrow text-right py-2.5 px-3 w-24">Weight (kg)</th>
                                                <th className="eyebrow text-right py-2.5 px-3 w-28">Rate (Rs/kg)</th>
                                                <th className="eyebrow text-right py-2.5 px-3 w-32">Cost (Rs)</th>
                                                <th className="w-8"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(form.filling_components || []).length === 0 && (
                                                <tr><td colSpan={5} className="text-center text-slate-400 py-4 text-[12px]">No components yet: click <b>Add Fill Component</b></td></tr>
                                            )}
                                            {(form.filling_components || []).map((fc, idx) => {
                                                const qty = parseFloat(fc.qty) || 0;
                                                const rate = parseFloat(fc.rate) || 0;
                                                const cost = qty * rate;
                                                const master = fillingMaster.find((m) => m.material_name === fc.name);
                                                const onMaterialChange = (val) => {
                                                    const m = fillingMaster.find((x) => x.material_name === val);
                                                    const patch = { name: val };
                                                    if (m) patch.rate = m.rate_rs_per_kg;
                                                    updateFillingComponent(idx, patch);
                                                };
                                                return (
                                                    <tr key={idx} className="border-b border-slate-100" data-testid={`fill-row-${idx}`}>
                                                        <td className="px-3 py-1.5">
                                                            <Select value={fc.name || ""} onChange={(e) => onMaterialChange(e.target.value)} data-testid={`fill-row-${idx}-material`}>
                                                                <option value="">Select material…</option>
                                                                {fillingMaster.map((m) => (
                                                                    <option key={m.id || m.material_name} value={m.material_name}>{m.material_name}</option>
                                                                ))}
                                                                <option value="OTHER">Other (free text)</option>
                                                            </Select>
                                                            {fc.name === "OTHER" && (
                                                                <TextInput className="mt-1" placeholder="Material name"
                                                                    value={fc.custom_name || ""}
                                                                    onChange={(e) => updateFillingComponent(idx, { custom_name: e.target.value, name: e.target.value })} />
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-1.5">
                                                            <NumberInput value={fc.qty} step="0.001"
                                                                onChange={(e) => updateFillingComponent(idx, { qty: e.target.value })}
                                                                data-testid={`fill-row-${idx}-weight`} className="text-right" />
                                                        </td>
                                                        <td className="px-3 py-1.5">
                                                            {master ? (
                                                                <div
                                                                    className="h-9 px-2.5 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-end font-mono tabular-nums text-[12px] text-slate-700"
                                                                    title="Auto-filled from the Filling Materials master. Pick 'Other (free text)' to type a rate by hand."
                                                                    data-testid={`fill-row-${idx}-rate`}
                                                                >
                                                                    {fmtINR(fc.rate || 0)}
                                                                </div>
                                                            ) : (
                                                                <NumberInput value={fc.rate} step="0.01"
                                                                    onChange={(e) => updateFillingComponent(idx, { rate: e.target.value })}
                                                                    data-testid={`fill-row-${idx}-rate`} className="text-right" />
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-1.5 text-right font-mono tabular-nums" data-testid={`fill-row-${idx}-cost`}>
                                                            {fmtINR(cost)}
                                                        </td>
                                                        <td className="px-3 py-1.5">
                                                            <button type="button" className="text-slate-400 hover:text-rose-600"
                                                                onClick={() => removeFillingComponent(idx)}
                                                                data-testid={`fill-row-${idx}-remove`}>
                                                                ×
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Summary box */}
                                <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
                                    <div className="flex justify-between text-[13px]">
                                        <span className="text-slate-700 font-medium">Total Fill Weight</span>
                                        <span className="font-mono tabular-nums font-semibold" data-testid="total-fill-weight">{fmtIN(totalFillWeight, { decimals: 3 })} kg</span>
                                    </div>
                                    <div className="flex justify-between text-[13px] border-t-2 border-slate-300 pt-2">
                                        <span className="text-slate-900 font-semibold">Total Filling Cost</span>
                                        <span className="font-mono tabular-nums font-bold text-[hsl(var(--copper))]" data-testid="total-filling-cost">{fmtINR(totalFillCost)}</span>
                                    </div>

                                    <div className="pt-3 border-t border-slate-200">
                                        <div className="eyebrow text-slate-500 mb-2">REFERENCE SPECS (optional)</div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <Field label="Shell Volume (cum)">
                                                <NumberInput value={form.shell_volume_cum} step="0.001"
                                                    onChange={(e) => setF({ shell_volume_cum: e.target.value })}
                                                    data-testid="shell-volume-cum" />
                                            </Field>
                                            <Field label="Pouring Density (kg/cum)">
                                                <NumberInput value={form.pouring_density_kg_cum} step="1"
                                                    onChange={(e) => setF({ pouring_density_kg_cum: e.target.value })}
                                                    data-testid="pouring-density" />
                                            </Field>
                                        </div>
                                        {calculatedFillMass !== null && (
                                            <div className="text-[12px] text-slate-600 mt-2">
                                                Calculated Fill Mass: <span className="font-mono tabular-nums font-semibold">{fmtIN(calculatedFillMass, { decimals: 0 })} kg</span>
                                                <span className="text-slate-400"> · reference only. Actual weight = sum of components above.</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
}

const gradeRateOrZero = (g) => g?.rate_per_kg || 0;

// ---------------- Section 3: Conversion ----------------
function SectionConversion({ form, setF, machines, conversionOps = [], addOp, updateOp, removeOp, computed }) {
    const activeOps = conversionOps.filter((o) => o.is_active);
    return (
        <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-1">3 · Conversion Cost</h2>
            <p className="text-[12px] text-slate-500 mb-4">Each manufacturing operation that runs on a machine. Different UOMs (per hour, per kg, lump, etc.) are supported.</p>
            <InfoBox>
                Conversion cost is the cost to run machines transforming raw steel into a finished part. Add each operation as a separate row: pick the Operation Name from the Conversion Operations master (Foundation Setup) or choose &quot;Other (free text)&quot; for anything not listed yet, pick the Machine, and the Rate auto-fills from its approved hourly rate (locked to prevent typos). Only the time/quantity for that operation needs entering by hand. Pick &quot;(manual)&quot; under Machine only if it isn&apos;t in the master list yet; that&apos;s the one case where Rate stays editable.
            </InfoBox>

            <div className="space-y-2 mb-4">
                {(form.operations || []).map((op, idx) => (
                    <div key={idx} className="rounded-md border border-slate-200 p-3 space-y-2.5">
                        {/* Row 1: identity — name, machine, UOM. Own row so long
                            machine codes and the UOM label never get clipped. */}
                        <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-5">
                                {(() => {
                                    const isKnownOp = activeOps.some((o) => o.operation_name === op.name);
                                    return (
                                        <Field label="Operation Name">
                                            <Select
                                                value={isKnownOp ? op.name : "OTHER"}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === "OTHER") {
                                                        updateOp(idx, { name: op._custom_name || "" });
                                                    } else {
                                                        const master = activeOps.find((o) => o.operation_name === val);
                                                        updateOp(idx, { name: val, ...(master?.default_uom ? { uom: master.default_uom } : {}) });
                                                    }
                                                }}
                                                className="h-8 text-[12px]"
                                            >
                                                <option value="OTHER">Other (free text)</option>
                                                {activeOps.map((o) => <option key={o.id} value={o.operation_name}>{o.operation_name}</option>)}
                                            </Select>
                                            {!isKnownOp && (
                                                <TextInput
                                                    className="mt-1 h-8 text-[12px]"
                                                    value={op.name}
                                                    onChange={(e) => updateOp(idx, { name: e.target.value, _custom_name: e.target.value })}
                                                    placeholder="Forging, Drilling, CNC…"
                                                />
                                            )}
                                        </Field>
                                    );
                                })()}
                            </div>
                            <div className="col-span-4">
                                <Field label={<>Machine {op._machine_auto_reason && !op._machine_manually_set && <AutoTip reason={op._machine_auto_reason} />}</>}>
                                    <Select value={op.machine_id || ""} onChange={(e) => updateOp(idx, { machine_id: e.target.value })} className="h-8 text-[12px]">
                                        <option value="">(manual)</option>
                                        {machines.map((m) => <option key={m.id} value={m.id}>{m.code}</option>)}
                                    </Select>
                                </Field>
                            </div>
                            <div className="col-span-3">
                                <Field label="UOM">
                                    <Select value={op.uom} onChange={(e) => updateOp(idx, { uom: e.target.value })} className="h-8 text-[12px]">
                                        {Object.keys(UOM_LABEL).map((u) => <option key={u} value={u}>{u}</option>)}
                                    </Select>
                                </Field>
                            </div>
                        </div>

                        {/* Row 2: numbers — qty/time, rate, cost, remove. Generous
                            width for the qty/time field specifically, since that's
                            the one number a user always has to enter by hand. */}
                        <div className="grid grid-cols-12 gap-2">
                            {/* Budget: Cost is always 3, Delete is always 2, leaving
                                exactly 7 columns for {Time, Parts/Box, Rate} depending
                                on UOM — LUMP has none of the qty fields (Rate takes all
                                7), PER_BOX has all three (3+2+2), everything else has
                                just Time + Rate (4+3). Every branch must sum to 12. */}
                            {op.uom !== "LUMP" && (
                                <div className={op.uom === "PER_BOX" ? "col-span-3" : "col-span-4"}>
                                    <Field label={UOM_LABEL[op.uom]?.split(" ")[0] || "Qty"}>
                                        <NumberInput value={op.qty_or_time} onChange={(e) => updateOp(idx, { qty_or_time: e.target.value })} className="h-8 text-[12px]" step="0.01" />
                                    </Field>
                                </div>
                            )}
                            {op.uom === "PER_BOX" && (
                                <div className="col-span-2">
                                    <Field label="Parts/Box">
                                        <NumberInput value={op.parts_per_box || ""} onChange={(e) => updateOp(idx, { parts_per_box: e.target.value })} className="h-8 text-[12px]" step="1" />
                                    </Field>
                                </div>
                            )}
                            <div className={op.uom === "LUMP" ? "col-span-7" : op.uom === "PER_BOX" ? "col-span-2" : "col-span-3"}>
                                <Field label={op.machine_id ? "Rate (auto)" : "Rate"}>
                                    {op.machine_id ? (
                                        <div className="h-8 px-2.5 rounded-md bg-slate-50 border border-slate-200 flex items-center font-mono tabular-nums text-[12px] text-slate-700" title="Auto-filled from the machine's approved rate. Pick '(manual)' under Machine to type a rate by hand.">
                                            {fmtINR(op.rate || 0)}
                                        </div>
                                    ) : (
                                        <NumberInput value={op.rate} onChange={(e) => updateOp(idx, { rate: e.target.value })} className="h-8 text-[12px]" step="0.01" />
                                    )}
                                </Field>
                            </div>
                            <div className="col-span-3 flex flex-col">
                                <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Cost</span>
                                <div className="h-8 flex items-center font-mono text-[12px] tabular-nums font-semibold text-[hsl(var(--copper))]">{fmtINR(op.cost || 0)}</div>
                            </div>
                            <div className="col-span-2 flex flex-col">
                                <span className="text-[11px] mb-1.5">&nbsp;</span>
                                <button onClick={() => removeOp(idx)} className="h-8 w-8 rounded-md text-red-500 hover:bg-red-50 flex items-center justify-center" title="Remove">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <SlateButton variant="outline" onClick={addOp} className="w-full">
                <Plus className="h-4 w-4" /> Add Operation
            </SlateButton>

            <div className="mt-4 flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
                <span className="text-sm font-medium text-slate-700">Total Conversion Cost</span>
                <span className="font-mono tabular-nums text-lg font-semibold text-[hsl(var(--copper))]">{fmtINR(computed.conversion_cost)}</span>
            </div>
        </Card>
    );
}

// ---------------- Section 4: Cutting ----------------
function SectionCutting({ form, setF, processRates, computed, autoTips = {}, manuallyEdited = {}, resetAuto }) {
    const rm = form.rm_category;
    const skip = rm === "ROUND_BAR" || rm === "FILL_MATERIAL";
    const thick = parseFloat(form.thickness_mm) || 0;
    const lookup = thick > 0 ? findCuttingRate(thick, processRates) : null;

    if (skip) {
        return (
            <Card className="p-6">
                <h2 className="font-display text-xl font-semibold mb-1">4 · Cutting Cost</h2>
                <div className="rounded-md bg-slate-50 border border-slate-200 p-6 text-center">
                    <Scissors className="h-7 w-7 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Cutting cost = <span className="font-mono">₹0</span> · not applicable for <span className="font-mono">{rm}</span>.</p>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-1">4 · Cutting Cost</h2>
            <p className="text-[12px] text-slate-500 mb-4">Cutting applies to sheet steel and tube parts. Rate is picked from the master and multiplied by perimeter × thickness.</p>

            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <Field
                        label={<>Cutting Process {autoTips.cutting_process && <AutoTip reason={autoTips.cutting_process} overridden={!!manuallyEdited.cutting_process} onReset={() => resetAuto?.("cutting_process")} />}</>}
                        required
                    >
                        <Select value={form.cutting_process} onChange={(e) => setF({ cutting_process: e.target.value })}>
                            <option value="LASER_CUTTING">Laser Cutting</option>
                            <option value="PLASMA_CUTTING">Plasma Cutting</option>
                            <option value="GAS_CUTTING">Gas Cutting</option>
                            <option value="SHEARING">Shearing</option>
                        </Select>
                    </Field>
                    <Field
                        label={<>Cut Perimeter (Mtr) {autoTips.cut_perimeter_mtr && <AutoTip reason={autoTips.cut_perimeter_mtr} overridden={!!manuallyEdited.cut_perimeter_mtr} onReset={() => resetAuto?.("cut_perimeter_mtr")} />}</>}
                        required
                        helper="Auto = 2×(W+L) for rectangular blanks; adjust for cut-outs"
                    >
                        <NumberInput value={form.cut_perimeter_mtr} onChange={(e) => setF({ cut_perimeter_mtr: e.target.value })} step="0.001" data-testid="cut-perimeter-mtr" />
                    </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Field label="Thickness (mm)" helper="Auto-filled from Section 2">
                        <NumberInput value={form.thickness_mm} onChange={(e) => setF({ thickness_mm: e.target.value })} step="0.1" />
                    </Field>
                    <Field label="Rate (₹/Mtr/mm)" helper={lookup ? `Master rate for ≤ ${lookup.used} mm` : "Auto-picked from master"}>
                        <div className="h-9 px-3 rounded-md bg-slate-50 border border-slate-200 flex items-center font-mono tabular-nums text-sm">
                            {lookup ? fmtINR(lookup.rate) : "—"}
                        </div>
                    </Field>
                </div>

                <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                    <div className="eyebrow text-slate-500 mb-2">LIVE CALCULATION</div>
                    <table className="w-full text-[12px]">
                        <tbody>
                            <tr><td className="text-slate-600">Cut Perimeter</td><td className="text-right font-mono tabular-nums">{fmtIN(parseFloat(form.cut_perimeter_mtr) || 0, { decimals: 3 })} Mtr</td></tr>
                            <tr><td className="text-slate-600">× Thickness</td><td className="text-right font-mono tabular-nums">{fmtIN(thick, { decimals: 2 })} mm</td></tr>
                            <tr><td className="text-slate-600">× Cutting Rate</td><td className="text-right font-mono tabular-nums">{lookup ? fmtINR(lookup.rate) : "₹0"}/Mtr/mm</td></tr>
                            <tr className="border-t-2 border-slate-300"><td className="font-semibold pt-2">Cutting Cost</td><td className="text-right font-mono tabular-nums font-bold text-[hsl(var(--copper))] pt-2">{fmtINR(computed.cutting_cost)}</td></tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Card>
    );
}

// ---------------- Section 5: Welding ----------------
function SectionWelding({ form, setF, processRates, addJoint, updateJoint, removeJoint, computed }) {
    return (
        <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-1">5 · Welding Cost</h2>
            <p className="text-[12px] text-slate-500 mb-4">Each weld joint listed separately. Fillet sizes always round UP to the next available rate; never under-cost.</p>

            <label className="inline-flex items-center gap-2 text-sm mb-4 cursor-pointer">
                <input
                    type="checkbox"
                    checked={!form.has_welding}
                    onChange={(e) => setF({ has_welding: !e.target.checked, weld_joints: e.target.checked ? [] : form.weld_joints })}
                    className="h-4 w-4 rounded border-slate-300 text-[hsl(var(--copper))] focus:ring-[hsl(var(--copper))]"
                />
                <span className="text-slate-700">This part has no welding</span>
            </label>

            {form.has_welding && (
                <>
                    <InfoBox>
                        <strong>Rule 4 · Round UP:</strong> Welding rates are looked up by fillet size. If your calculated fillet is between two sizes (e.g. 4.2 mm), the larger rate (5 mm) is always used.
                    </InfoBox>

                    <div className="space-y-2 mb-4">
                        {(form.weld_joints || []).map((j, idx) => {
                            const fillet = parseFloat(j.fillet_mm) || 0;
                            const lookup = fillet > 0 ? findWeldingRate(fillet, processRates) : null;
                            const cost = lookup ? (parseFloat(j.length_mtr) || 0) * lookup.rate * (parseInt(j.qty, 10) || 1) : 0;
                            return (
                                <div key={idx} className="rounded-md border border-slate-200 p-3">
                                    <div className="grid grid-cols-12 gap-2 items-end">
                                        <div className="col-span-3">
                                            <Field label="Joint Description">
                                                <TextInput value={j.description || ""} onChange={(e) => updateJoint(idx, { description: e.target.value })} className="h-8 text-[12px]" placeholder="e.g. floor to cross member" />
                                            </Field>
                                        </div>
                                        <div className="col-span-2">
                                            <Field label="Length (Mtr)">
                                                <NumberInput value={j.length_mtr} onChange={(e) => updateJoint(idx, { length_mtr: e.target.value })} className="h-8 text-[12px]" step="0.01" />
                                            </Field>
                                        </div>
                                        <div className="col-span-2">
                                            <Field label={<>Fillet (mm) {j._fillet_auto_reason && !j._fillet_manually_set && <AutoTip reason={j._fillet_auto_reason} />}</>}>
                                                <NumberInput value={j.fillet_mm} onChange={(e) => updateJoint(idx, { fillet_mm: e.target.value })} className="h-8 text-[12px]" step="0.1" />
                                            </Field>
                                        </div>
                                        <div className="col-span-2 flex flex-col">
                                            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Rate</span>
                                            <div className="h-8 flex items-center font-mono text-[12px] tabular-nums">
                                                {lookup ? <>{fmtINR(lookup.rate)}<span className="text-[10px] text-slate-400 ml-1">(↑{lookup.used}mm)</span></> : "—"}
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <Field label="Qty">
                                                <NumberInput value={j.qty} onChange={(e) => updateJoint(idx, { qty: e.target.value })} className="h-8 text-[12px]" step="1" min="1" />
                                            </Field>
                                        </div>
                                        <div className="col-span-1 flex flex-col">
                                            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Cost</span>
                                            <div className="h-8 flex items-center font-mono text-[12px] tabular-nums font-semibold text-[hsl(var(--copper))]">{fmtINR(cost)}</div>
                                        </div>
                                        <div className="col-span-1 flex justify-end">
                                            <button onClick={() => removeJoint(idx)} className="h-8 w-8 rounded-md text-red-500 hover:bg-red-50 flex items-center justify-center"><X className="h-4 w-4" /></button>
                                        </div>
                                    </div>
                                    {lookup && fillet > 0 && fillet !== lookup.used && (
                                        <div className="text-[11px] text-amber-700 mt-2 flex items-center gap-1">
                                            <AlertTriangle className="h-3 w-3" /> Fillet {fillet}mm rounded UP to {lookup.used}mm rate (Rule 4)
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <SlateButton variant="outline" onClick={addJoint} className="w-full">
                        <Plus className="h-4 w-4" /> Add Weld Joint
                    </SlateButton>

                    <div className="mt-4 flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
                        <span className="text-sm font-medium">Total Welding Cost</span>
                        <span className="font-mono tabular-nums text-lg font-semibold text-[hsl(var(--copper))]">{fmtINR(computed.welding_cost)}</span>
                    </div>
                </>
            )}
        </Card>
    );
}

// ---------------- Section 6: Result ----------------
function SectionResult({ form, setF, computed, variance, selectedCustomer, onSaveDraft, onVerify, submitting }) {
    const { quoteDays, isStale } = useMemo(() => {
        if (!form.quote_date) return { quoteDays: 0, isStale: false };
        const days = Math.floor((Date.now() - new Date(form.quote_date).getTime()) / (1000 * 60 * 60 * 24));
        return { quoteDays: days, isStale: days > 180 };
    }, [form.quote_date]);

    const [suppliers, setSuppliers] = useState([]);
    useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

    return (
        <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-1">6 · Should-Cost & Variance</h2>
            <p className="text-[12px] text-slate-500 mb-5">Final assembly with ZBC standard allowances + variance against supplier quote.</p>

            {/* ============ P0.2 · TOOLING / CAPEX ============ */}
            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-4 mb-5" data-testid="capex-panel">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="eyebrow text-slate-500">TOOLING / CAPEX (optional)</div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            One-time tool / fixture / die cost amortised across the projected annual volume × amortisation period.
                        </p>
                    </div>
                    {computed.capex_per_piece > 0 && (
                        <div className="text-right">
                            <div className="eyebrow text-slate-500">CAPEX / PIECE</div>
                            <div className="font-mono tabular-nums font-semibold text-[hsl(var(--copper))]">{fmtINR(computed.capex_per_piece)}</div>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Tooling Cost (₹)" helper="One-time upfront tool / fixture / die cost">
                        <NumberInput value={form.tooling_cost_rs} onChange={(e) => setF({ tooling_cost_rs: e.target.value })} step="1000" placeholder="e.g. 250000" data-testid="tooling-cost-rs" />
                    </Field>
                    <Field label="Annual Volume (pcs)" helper="Projected pieces per year">
                        <NumberInput value={form.annual_volume} onChange={(e) => setF({ annual_volume: e.target.value })} step="100" placeholder="e.g. 12000" data-testid="annual-volume" />
                    </Field>
                    <Field label="Amortisation (years)" helper="Default 3 years">
                        <NumberInput value={form.amortization_years} onChange={(e) => setF({ amortization_years: e.target.value })} step="1" min="1" data-testid="amort-years" />
                    </Field>
                </div>
                {form.tooling_cost_rs > 0 && form.annual_volume > 0 && (
                    <div className="mt-3 text-[11px] text-slate-600 font-mono">
                        = ₹{Number(form.tooling_cost_rs).toLocaleString("en-IN")} ÷ ({Number(form.annual_volume).toLocaleString("en-IN")} × {form.amortization_years || 3}) = <strong>{fmtINR(computed.capex_per_piece)}</strong> per piece
                    </div>
                )}
            </div>

            {/* ============ P0.3 · CHILD SUB-ASSEMBLIES ============ */}
            <ChildZBCPanel form={form} setF={setF} computed={computed} />

            <div className="eyebrow text-slate-500 mb-3">COST ASSEMBLY</div>
            <table className="w-full text-[13px] mb-6">
                <tbody>
                    <tr><td className="py-1.5 text-slate-600">Net Material Cost</td><td className="text-right font-mono tabular-nums">{fmtINR(computed.net_material_cost)}</td></tr>
                    <tr><td className="py-1.5 text-slate-600">Conversion Cost</td><td className="text-right font-mono tabular-nums">{fmtINR(computed.conversion_cost)}</td></tr>
                    <tr><td className="py-1.5 text-slate-600">Cutting Cost</td><td className="text-right font-mono tabular-nums">{fmtINR(computed.cutting_cost)}</td></tr>
                    <tr><td className="py-1.5 text-slate-600">Welding Cost</td><td className="text-right font-mono tabular-nums">{fmtINR(computed.welding_cost)}</td></tr>
                    <tr className="border-t border-slate-300"><td className="py-2 font-semibold">Base Cost</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(computed.base_cost)}</td></tr>
                    <tr><td className="py-1 text-slate-500 text-[11px]">+ ICC (1.0% on RM)</td><td className="text-right font-mono tabular-nums text-[11px] text-slate-500">{fmtINR(computed.icc_amount)}</td></tr>
                    <tr><td className="py-1 text-slate-500 text-[11px]">+ Rejection (0.25%)</td><td className="text-right font-mono tabular-nums text-[11px] text-slate-500">{fmtINR(computed.rejection_amount)}</td></tr>
                    <tr><td className="py-1 text-slate-500 text-[11px]">+ Tool Maintenance (1.0%)</td><td className="text-right font-mono tabular-nums text-[11px] text-slate-500">{fmtINR(computed.tool_maint_amount)}</td></tr>
                    <tr><td className="py-1 text-slate-500 text-[11px]">+ Profit &amp; OH (5.0%)</td><td className="text-right font-mono tabular-nums text-[11px] text-slate-500">{fmtINR(computed.profit_oh_amount)}</td></tr>
                    {computed.capex_per_piece > 0 && (
                        <tr><td className="py-1.5 text-slate-700">+ CAPEX / piece (tooling amortisation)</td><td className="text-right font-mono tabular-nums text-slate-700">{fmtINR(computed.capex_per_piece)}</td></tr>
                    )}
                    {computed.children_cost > 0 && (
                        <tr><td className="py-1.5 text-slate-700">+ Sub-assembly Roll-up ({(form.child_zbc_refs || []).length} children)</td><td className="text-right font-mono tabular-nums text-slate-700">{fmtINR(computed.children_cost)}</td></tr>
                    )}
                    <tr className="border-t-2 border-slate-900 bg-[hsl(var(--copper-soft))]">
                        <td className="py-3 font-display text-base font-semibold">SHOULD-COST</td>
                        <td className="text-right font-mono tabular-nums font-bold text-2xl text-[hsl(var(--copper))]">{fmtINR(computed.should_cost)}</td>
                    </tr>
                </tbody>
            </table>

            <div className="text-[11px] text-slate-500 mb-6 border-l-2 border-slate-200 pl-3">
                The allowance percentages above are <strong>ZBC standard norms</strong> used for should-cost comparison only.
                Customer-specific commercials (Kobelco / Volvo / VECV / FES variants) are applied in the RFQ module, not here.
            </div>

            <div className="eyebrow text-slate-500 mb-3">SUPPLIER QUOTE</div>
            <div className="grid grid-cols-3 gap-4 mb-5">
                <Field label="Supplier" helper="Picked from the Suppliers master, Foundation Setup › Suppliers">
                    <Select
                        value={form.supplier_id || ""}
                        onChange={(e) => {
                            const s = suppliers.find((x) => x.id === e.target.value);
                            setF({ supplier_id: e.target.value || null, supplier_name: s ? s.name : "" });
                        }}
                    >
                        <option value="">Select supplier…</option>
                        {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Select>
                </Field>
                <Field label="Quote Amount (₹)">
                    <NumberInput value={form.supplier_quote} onChange={(e) => setF({ supplier_quote: e.target.value })} step="0.01" />
                </Field>
                <Field label="Quote Date">
                    <TextInput type="date" value={form.quote_date ? String(form.quote_date).slice(0, 10) : ""} onChange={(e) => setF({ quote_date: e.target.value })} />
                </Field>
            </div>

            {isStale && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-5 text-[12px] text-amber-900 flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                    This quote is {quoteDays} days old. Consider requesting a fresh quote before finalising.
                </div>
            )}

            {variance.badge && (
                <div className="rounded-lg border-2 border-slate-200 overflow-hidden mb-5">
                    <div className="grid grid-cols-3 divide-x divide-slate-200">
                        <div className="px-4 py-3 bg-slate-50">
                            <div className="eyebrow text-slate-500">SHOULD-COST</div>
                            <div className="font-mono tabular-nums text-lg font-semibold">{fmtINR(computed.should_cost)}</div>
                        </div>
                        <div className="px-4 py-3">
                            <div className="eyebrow text-slate-500">SUPPLIER QUOTE</div>
                            <div className="font-mono tabular-nums text-lg font-semibold">{fmtINR(form.supplier_quote)}</div>
                        </div>
                        <div className="px-4 py-3 bg-[hsl(var(--copper-soft))]">
                            <div className="eyebrow text-slate-600">VARIANCE</div>
                            <div className="flex items-center gap-2 mt-0.5">
                                <VarianceBadge badge={variance.badge} pct={variance.pct} />
                                <span className="font-mono tabular-nums text-sm">{variance.amount >= 0 ? "+" : ""}{fmtINR(variance.amount)}</span>
                            </div>
                        </div>
                    </div>
                    <VarianceMessage badge={variance.badge} />
                </div>
            )}
        </Card>
    );
}

function VarianceMessage({ badge }) {
    const messages = {
        GREEN: { tone: "bg-emerald-50 text-emerald-900 border-emerald-200", text: "✓ Fair Price: Supplier quoting within 5% of should-cost." },
        AMBER: { tone: "bg-amber-50 text-amber-900 border-amber-200", text: "⚠ Review: Supplier quoting 5–15% above should-cost. Investigate before approving." },
        RED: { tone: "bg-red-50 text-red-900 border-red-200", text: "⚡ Challenge: Supplier quoting more than 15% above should-cost. Strong negotiation lever." },
        FLAG: { tone: "bg-red-100 text-red-900 border-red-300", text: "🚩 Investigate: Supplier quoting BELOW should-cost. Verify material grade, process, or our estimate." },
    };
    const m = messages[badge];
    if (!m) return null;
    return <div className={`px-4 py-2.5 border-t text-[12px] ${m.tone}`}>{m.text}</div>;
}

function DuplicateCheckBanner({ partNumber, customerId, onContinue }) {
    const [existing, setExisting] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setDismissed(false);
        setExisting(null);
        const pn = (partNumber || "").trim();
        if (!pn || !customerId || pn.length < 3) return;
        let cancelled = false;
        const timer = setTimeout(() => {
            api.get(`/zbc-check-duplicate?part_number=${encodeURIComponent(pn)}&customer_id=${encodeURIComponent(customerId)}`)
                .then((r) => { if (!cancelled && r.data?.duplicate) setExisting(r.data.existing); })
                .catch(() => { /* noop */ });
        }, 350);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [partNumber, customerId]);

    if (!existing || dismissed) return null;
    return (
        <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-4 mb-4 flex items-start gap-3" data-testid="zbc-duplicate-banner">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-[13px]">
                <div className="font-semibold text-amber-900">Part number <span className="font-mono">{existing.part_number}</span> already exists for this customer</div>
                <div className="text-[12px] text-amber-800 mt-0.5">
                    Created by <strong>{existing.created_by_name || "—"}</strong> on {fmtDate(existing.created_at)} · Status <span className="font-mono">{existing.status}</span> · Variance <span className="font-mono">{existing.variance_badge || "—"}</span>
                </div>
                <div className="flex gap-2 mt-2">
                    <button onClick={() => navigate(`/zbc/${existing.id}`)} className="h-7 px-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-[12px] font-medium" data-testid="zbc-duplicate-view">View existing ZBC →</button>
                    <button onClick={() => { setDismissed(true); onContinue && onContinue(); }} className="h-7 px-3 rounded-md border border-amber-300 hover:bg-amber-100 text-amber-900 text-[12px]" data-testid="zbc-duplicate-continue">Continue anyway (revision)</button>
                </div>
            </div>
        </div>
    );
}


// ---------------- ChildZBCPanel (P0.3) ----------------
function ChildZBCPanel({ form, setF, computed }) {
    const [query, setQuery] = useState("");
    const [candidates, setCandidates] = useState([]);
    const [searching, setSearching] = useState(false);
    const [open, setOpen] = useState((form.child_zbc_refs || []).length > 0);

    const refs = form.child_zbc_refs || [];

    useEffect(() => {
        if (!query || query.trim().length < 2) { setCandidates([]); return; }
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/zbcs?search=${encodeURIComponent(query.trim())}&limit=20`);
                setCandidates((r.data || []).filter((z) => z.status === "VERIFIED" && z.id !== form.id));
            } catch { /* noop */ } finally { setSearching(false); }
        }, 350);
        return () => clearTimeout(t);
    }, [query, form.id]);

    const addChild = (z) => {
        // Reject direct self-reference and duplicates
        if (z.id === form.id) return;
        if (refs.some((r) => r.child_zbc_id === z.id)) return;
        setF({
            child_zbc_refs: [...refs, {
                child_zbc_id: z.id, qty: 1,
                child_part_number: z.part_number, child_part_name: z.part_name,
                child_should_cost: z.should_cost || 0,
            }],
        });
        setQuery(""); setCandidates([]);
    };
    const updateChild = (idx, patch) => {
        const next = [...refs]; next[idx] = { ...next[idx], ...patch };
        setF({ child_zbc_refs: next });
    };
    const removeChild = (idx) => setF({ child_zbc_refs: refs.filter((_, i) => i !== idx) });

    return (
        <div className="rounded-md border border-slate-200 bg-slate-50/40 p-4 mb-5" data-testid="children-panel">
            <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between mb-1 text-left" data-testid="children-toggle">
                <div>
                    <div className="eyebrow text-slate-500">SUB-ASSEMBLIES (optional)</div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                        Link existing verified ZBCs as children of this assembly. Each child&apos;s Should-Cost × Qty rolls into this ZBC.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {refs.length > 0 && (
                        <span className="rounded-full bg-[hsl(var(--copper-soft))] text-[hsl(var(--copper))] text-[11px] font-semibold px-2 py-0.5" data-testid="children-count">{refs.length}</span>
                    )}
                    {computed.children_cost > 0 && <span className="font-mono text-[hsl(var(--copper))] font-semibold text-[13px]">{fmtINR(computed.children_cost)}</span>}
                    <span className="text-slate-400 text-[11px]">{open ? "▲ Hide" : "▼ Add / view"}</span>
                </div>
            </button>

            {open && (
                <>
                    <div className="relative mt-3">
                        <TextInput
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search verified ZBC by part number / name…"
                            data-testid="children-search"
                        />
                        {searching && <div className="absolute right-2 top-2 text-[11px] text-slate-400">searching…</div>}
                        {candidates.length > 0 && (
                            <div className="absolute z-30 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-56 overflow-auto">
                                {candidates.map((z) => (
                                    <button key={z.id} type="button" onClick={() => addChild(z)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`children-add-${z.id}`}>
                                        <div className="text-[12px] font-medium text-slate-800">{z.part_number} · {z.part_name}</div>
                                        <div className="text-[10.5px] text-slate-500 font-mono">₹{(z.should_cost || 0).toFixed(2)} · {z.customer_name}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {refs.length > 0 && (
                        <table className="w-full mt-3 text-[12px]" data-testid="children-table">
                            <thead>
                                <tr className="text-left text-slate-500 eyebrow border-b">
                                    <th className="py-1.5">Child Part</th>
                                    <th className="py-1.5">Should-Cost (₹)</th>
                                    <th className="py-1.5 w-16">Qty</th>
                                    <th className="py-1.5">Line Cost (₹)</th>
                                    <th className="w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {refs.map((ch, idx) => (
                                    <tr key={`${ch.child_zbc_id}-${idx}`} className="border-b border-slate-100 last:border-0">
                                        <td className="py-1.5">
                                            <div className="font-medium">{ch.child_part_number}</div>
                                            <div className="text-[10.5px] text-slate-500">{ch.child_part_name}</div>
                                        </td>
                                        <td className="py-1.5 font-mono tabular-nums">{fmtINR(ch.child_should_cost || 0)}</td>
                                        <td className="py-1.5">
                                            <NumberInput
                                                value={ch.qty}
                                                onChange={(e) => updateChild(idx, { qty: parseFloat(e.target.value) || 0 })}
                                                className="h-7 text-[12px] w-16" step="1" min="0"
                                                data-testid={`children-qty-${idx}`}
                                            />
                                        </td>
                                        <td className="py-1.5 font-mono tabular-nums text-slate-700">
                                            {fmtINR((ch.child_should_cost || 0) * (ch.qty || 0))}
                                        </td>
                                        <td className="py-1.5 text-right">
                                            <button type="button" onClick={() => removeChild(idx)} className="text-red-500 hover:text-red-700 text-[11px]" data-testid={`children-remove-${idx}`}>✕</button>
                                        </td>
                                    </tr>
                                ))}
                                <tr className="bg-slate-100 font-semibold">
                                    <td colSpan={3} className="py-2 text-right pr-2">Sub-Assembly Roll-up</td>
                                    <td className="py-2 font-mono tabular-nums text-[hsl(var(--copper))]">{fmtINR(computed.children_cost)}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    )}

                    {refs.length === 0 && (
                        <div className="mt-3 text-[11.5px] text-slate-500 italic text-center py-3 border-2 border-dashed border-slate-200 rounded-md">
                            No sub-assemblies. Search above to link verified ZBCs as children.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
